// @flow
import * as React from 'react';
import {defineMessage} from 'react-intl';
import session from '../session';
import {
  automaticTaxStep,
  lineItemsStep,
  paymentMethodsStep,
} from '../html/steps';
import {
  DefaultCards,
  RunServerCommandShell,
} from '../../../components/left-col';
import type {StepsConfig} from '../../../data/GuideTypes';

export default ([
  {
    file: 'server',
    id: 'setup',
    section: 'setup-stripe',
  },
  {
    file: 'server',
    id: 'create',
    section: 'setup-stripe',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.react.create.heading',
        defaultMessage: 'Create a Checkout Session',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.react.create.body',
        defaultMessage: `Add an endpoint on your server that creates a <a>Checkout Session</a>. A Checkout Session controls what your customer sees in the Stripe-hosted payment page such as line items, the order amount and currency, and acceptable payment methods.`,
      }),
      values: {
        a: (str) => (
          <a href="/docs/api/checkout/sessions" target="_blank">
            {str}
          </a>
        ),
      },
    },
    response: session.react,
  },
  lineItemsStep,
  paymentMethodsStep,
  {
    file: 'server',
    id: 'mode',
    section: 'setup-stripe',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.react.mode.heading',
        defaultMessage: 'Choose the mode',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.react.mode.body',
        defaultMessage: `Checkout has three modes: <code>payment</code>, <code>subscription</code>, or <code>setup</code>. Use <code>payment</code> mode for one-time purchases. Learn more about <subscriptionA>subscription</subscriptionA> and <setupA>setup</setupA> modes in the docs.`,
      }),
      values: {
        subscriptionA: (str) => (
          <a
            href="/docs/payments/checkout/set-up-a-subscription"
            target="_blank"
          >
            {str}
          </a>
        ),
        setupA: (str) => (
          <a href="/docs/payments/save-and-reuse#checkout" target="_blank">
            {str}
          </a>
        ),
        code: (str) => <code>{str}</code>,
      },
    },
  },
  {
    file: 'server',
    id: 'urls',
    section: 'setup-stripe',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.react.urls.heading',
        defaultMessage: 'Supply success and cancel URLs',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.react.urls.body',
        defaultMessage: `Specify the URLs Stripe should redirect to when your customer completes or cancels the Checkout Session. You can handle both the success and canceled states with the same URL.`,
      }),
    },
  },
  {
    file: 'server',
    id: 'redirect',
    section: 'setup-stripe',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.react.redirect.heading',
        defaultMessage: 'Redirect to Checkout',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.react.redirect.body',
        defaultMessage: `After creating the session, redirect your customer to the Checkout page's URL returned in the response.`,
      }),
    },
  },
  {
    id: 'preview-page',
    section: 'build-page',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.react.preview-page.heading',
        defaultMessage: 'Add an order preview page',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.react.preview-page.body',
        defaultMessage:
          "Add a page to show a preview of the customer's order. Allow the customer to review or modify their order—once a customer is sent to the Checkout page, the order is final and cannot be modified without creating a new Checkout Session.",
      }),
    },
  },
  {
    id: 'add-button',
    section: 'build-page',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.react.add-button.heading',
        defaultMessage: 'Add a checkout button',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.react.add-button.body',
        defaultMessage:
          'Add a button to your order preview page. When your customer clicks this button, they are redirected to the Stripe-hosted payment form.',
      }),
    },
  },
  {
    id: 'run-server',
    section: 'testing',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.react.run-server.heading',
        defaultMessage: 'Run the application',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.react.run-server.body',
        defaultMessage:
          'Start your server and navigate to <a>http://localhost:3000/checkout</a>',
      }),
      values: {
        a: (str) => <a href="http://localhost:3000/checkout">{str}</a>,
        code: (str) => <code>{str}</code>,
      },
    },
    Component: RunServerCommandShell,
  },
  {
    id: 'testing',
    section: 'testing',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.react.testing.heading',
        defaultMessage: 'Try it out',
      }),
    },
    testing: true,
    body: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.react.testing.body',
        defaultMessage:
          'Click the checkout button to be redirected to the Checkout page. Use any of these test cards to simulate a payment.',
      }),
    },
    Component: DefaultCards,
  },
  {
    extension: 'extra',
    id: 'customize',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.react.customize.heading',
        defaultMessage: 'Customize the checkout page',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.checkout.react.customize.body',
        defaultMessage:
          "You can customize the appearance of the hosted Checkout page by adding your logo and color theme in the Dashboard's <a>branding settings</a>, or by using the API to activate additional features like address collection or prefilling customer details.",
      }),
      values: {
        a: (str) => (
          <a
            href="https://dashboard.stripe.com/settings/branding"
            target="_blank"
          >
            {str}
          </a>
        ),
      },
    },
    steps: [
      {
        id: 'prefill-email',
        heading: {
          message: defineMessage({
            id: 'docsApp.ib.checkout.react.prefill-email.heading',
            defaultMessage: 'Prefill customer data',
          }),
        },
        body: {
          message: defineMessage({
            id: 'docsApp.ib.checkout.react.prefill-email.body',
            defaultMessage:
              "Use <code>customer_email</code> to prefill the customer's email address in the email input field. You can also pass a <a>Customer</a> ID to <code>customer</code> field to prefill the email address field with the email stored on the Customer.",
          }),
          values: {
            a: (str) => (
              <a href="/docs/api/customers" target="_blank">
                {str}
              </a>
            ),
            code: (str) => <code>{str}</code>,
          },
        },
      },
      {
        id: 'submit-button',
        heading: {
          message: defineMessage({
            id: 'docsApp.ib.checkout.react.submit-button.heading',
            defaultMessage: 'Pick a submit button',
          }),
        },
        body: {
          message: defineMessage({
            id: 'docsApp.ib.checkout.react.submit-button.body',
            defaultMessage:
              'Configure the copy displayed on the Checkout submit button by setting the <code>submit_type</code>. There are four different submit types.',
          }),
          values: {
            code: (str) => <code>{str}</code>,
          },
        },
      },
      {
        id: 'address',
        heading: {
          message: defineMessage({
            id: 'docsApp.ib.checkout.react.address.heading',
            defaultMessage: 'Collect billing and shipping details',
          }),
        },
        body: {
          message: defineMessage({
            id: 'docsApp.ib.checkout.react.address.body',
            defaultMessage:
              "Use <code>billing_address_collection</code> and <code>shipping_address_collection</code> to collect your customer's address. <code>shipping_address_collection</code> requires a list of <code>allowed_countries</code>. Checkout displays the list of allowed countries in a dropdown on the page.",
          }),
          values: {
            code: (str) => <code>{str}</code>,
          },
        },
      },
      {
        id: 'testing-customized',
        section: 'testing',
        heading: {
          message: defineMessage({
            id: 'docsApp.ib.checkout.react.testing-customized.heading',
            defaultMessage: 'Preview the customized page',
          }),
        },
        testing: true,
        body: {
          message: defineMessage({
            id: 'docsApp.ib.checkout.react.testing-customized.body',
            defaultMessage:
              'Click the checkout button in the preview on the right to see a sample Stripe Checkout page with these additional fields. <a>Read our docs</a> for all the ways you can customize Checkout—we continuously add new features.',
          }),
          values: {
            a: (str) => (
              <a href="/docs/payments/checkout/customization" target="_blank">
                {str}
              </a>
            ),
          },
        },
      },
    ],
  },
  automaticTaxStep,
]: StepsConfig);
